import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { MarketQuote, Product } from '@prody/shared';
import {
  Button, CrateLabel, EmptyState, IconPlus, Input, ListPane, MoneyText,
  PageHeader, Segmented, Stamp, Toolbar, Workbench,
} from '~/design-system';
import { useBusiness } from '~/platform/tenancy/BusinessProvider';
import { useProducts, useQuotes } from '~/platform/data/queries';
import { productName } from '~/platform/domain/presentation';
import './catalog.css';

/* ==================================================================
   Catalog.

   The one screen in Prody that is a grid of cards rather than a
   table, because here the crate label *is* the record: a buyer
   scanning the catalog is looking for origin, calibre and today's
   price, which is exactly what a printed produce label carries.

   Every card is the same `CrateLabel` primitive the palette, the
   tables and the detail headers use, at a larger size.
   ================================================================== */

type View = 'grid' | 'list';

export default function CatalogPage() {
  const { t, i18n } = useTranslation(['catalog', 'common', 'market']);
  const { business, format } = useBusiness();
  const [search, setSearch] = useState('');
  const [view, setView] = useState<View>('grid');

  const { data: products = [], isLoading } = useProducts(business.id, search ? { search } : undefined);
  const { data: quotes = [] } = useQuotes(business.id, format.tradingDay());

  const quoteByProduct = useMemo(
    () => new Map(quotes.map((q) => [q.productId as string, q])),
    [quotes],
  );

  const locale = i18n.resolvedLanguage ?? 'en';

  return (
    <Workbench>
      <PageHeader
        eyebrow={t('eyebrow')}
        title={t('title')}
        count={isLoading ? undefined : t('common:labels.results', { count: products.length })}
        actions={
          <Button variant="primary" icon={<IconPlus />}>
            {t('products:newProduct')}
          </Button>
        }
      />

      <Toolbar
        trailing={
          <Segmented<View>
            label={t('common:actions.filter')}
            size="sm"
            value={view}
            onChange={setView}
            options={[
              { value: 'grid', label: '▦' },
              { value: 'list', label: '≡' },
            ]}
          />
        }
      >
        <Input
          type="search"
          placeholder={t('common:actions.search')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ inlineSize: 260 }}
        />
      </Toolbar>

      <ListPane>
        {!isLoading && products.length === 0 ? (
          <EmptyState
            title={search ? t('common:empty.noResults') : t('empty')}
            body={search ? t('common:empty.noResultsBody') : t('emptyBody')}
          />
        ) : (
          <div className="pd-catalog" data-view={view}>
            {products.map((product) => (
              <ProductCard
                key={product.id}
                product={product}
                quote={quoteByProduct.get(product.id as string)}
                locale={locale}
                size={view === 'grid' ? 'md' : 'sm'}
              />
            ))}
          </div>
        )}
      </ListPane>
    </Workbench>
  );
}

function ProductCard({
  product,
  quote,
  locale,
  size,
}: {
  product: Product;
  quote: MarketQuote | undefined;
  locale: string;
  size: 'sm' | 'md';
}) {
  const { t } = useTranslation(['catalog', 'common']);
  const { format } = useBusiness();

  const facts: { label: string; value: string }[] = [];
  if (product.traits.origin?.region) {
    facts.push({ label: t('origin'), value: product.traits.origin.region });
  }
  if (product.traits.grading?.calibre) {
    facts.push({ label: t('calibre'), value: product.traits.grading.calibre });
  }
  if (product.traits.perishable) {
    facts.push({
      label: t('shelfLife'),
      value: t('shelfLifeDays', { count: product.traits.perishable.shelfLifeDays }),
    });
  }
  facts.push({ label: t('common:labels.unit'), value: format.quantity({ value: 1, unit: product.salesUnit }) });

  return (
    <CrateLabel
      handlingClass={product.handlingClass}
      code={product.sku}
      title={productName(product, locale)}
      subtitle={product.traits.origin?.certification ?? undefined}
      size={size}
      facts={facts}
      stamp={
        product.traits.catchWeight ? (
          <Stamp tone="info" size="sm" title={t('catchWeightHint')}>
            {t('catchWeight')}
          </Stamp>
        ) : product.status !== 'active' ? (
          <Stamp tone="neutral" size="sm">
            {t(`status.${product.status}`)}
          </Stamp>
        ) : undefined
      }
      figures={
        quote ? (
          <>
            <MoneyText value={quote.sell} previous={quote.previousSell} size="lg" />
            <span className="stencil">{t('market:todaySell')}</span>
          </>
        ) : (
          <span className="faint num">—</span>
        )
      }
      onClick={() => undefined}
    />
  );
}
